import { useState, useEffect, useRef, useCallback } from "react";
import "./Filter.css";

type FilterKeys = "location" | "priceRange" | "hotelType" | "category";
type FilterSelectProps = {
  onFilterChange: (filters: Record<FilterKeys, string>) => void;
};

function FilterSelect({ onFilterChange }: FilterSelectProps) {
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [selectedValues, setSelectedValues] = useState({
    location: "მდებარეობა",
    priceRange: "ფასის დიაპაზონი",
    hotelType: "სასტუმროს ტიპი",
    category: "კატეგორია",
  });

  const dropdownRefs = {
    location: useRef<HTMLDivElement>(null),
    priceRange: useRef<HTMLDivElement>(null),
    hotelType: useRef<HTMLDivElement>(null),
    category: useRef<HTMLDivElement>(null),
  };

  const debounce = (func: (...args: any[]) => void, delay: number) => {
    let timer: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timer);
      timer = setTimeout(() => func(...args), delay);
    };
  };

  const debouncedFilterHandler = useCallback(debounce(onFilterChange, 250), [onFilterChange]);

  useEffect(() => {
    debouncedFilterHandler(selectedValues); // Trigger debounced callback with the current selected values
  }, [selectedValues, debouncedFilterHandler]);

  useEffect(() => {
    const closeDropdowns = (event: MouseEvent) => {
      Object.entries(dropdownRefs).forEach(([key, ref]) => {
        if (ref.current && !ref.current.contains(event.target as Node)) {
          setActiveDropdown(null);
        }
      });
    };
    document.addEventListener("click", closeDropdowns);
    return () => document.removeEventListener("click", closeDropdowns);
  }, []);

  const toggleDropdown = (dropdown: string, event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    setActiveDropdown(activeDropdown === dropdown ? null : dropdown);
  };

  const handleOptionClick = (type: string, option: string) => {
    setSelectedValues((prev) => {
      const newSelectedValues = { ...prev, [type]: option };
      // We update selected values here, and the effect hook will trigger the debounced filter change.
      return newSelectedValues;
    });
    setActiveDropdown(null); // Close the dropdown after selecting an option
  };

  const dropdownOptions = {
    location: ["ყველა",
      "თბილისი",
      "ბათუმი",
        "ქუთაისი",
        "ბაკურიანი",
        "ქობულეთი",
        "მცხეთა",
        "ყაზბეგი","გუდაური",
        "მესტია", "ამბროლაური",
        "თელავი","სიღნაღი"],
    priceRange: ["ნებისმიერი","0-50₾", "50-100₾", "100₾+"],
    hotelType: ["ყველა","ბუტიკი", "კოტეჯი", "სასტუმრო"]
  };

  return (
    <div className="filter">
      <div className="filter__controls">
        {Object.keys(dropdownOptions).map((key) => (
          <div
            key={key}
            className={`filter-select ${activeDropdown === key ? "active" : ""}`}
            onClick={(e) => toggleDropdown(key, e)}
            ref={dropdownRefs[key as keyof typeof dropdownRefs]}
          >
            <input type="text" name={key} value={selectedValues[key as keyof typeof selectedValues]} readOnly />
            <img src="https://bato-web-agency.github.io/bato-shared/table-view/img/select.svg" alt="Search" />
            <ul className="filter-select__options">
              {dropdownOptions[key as keyof typeof dropdownOptions].map((option) => (
                <li
                  key={option}
                  tabIndex={0}
                  className={selectedValues[key as keyof typeof selectedValues] === option ? "selected" : ""}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOptionClick(key, option); // Handle the option click and update state
                  }}
                >
                  {option}
                </li>
              ))}
            </ul>
          </div>
        ))}
        <button className="clear-filters">გასუფთავება <i className="fa-solid fa-trash"></i></button>
      </div>
    </div>
  );
}

export default FilterSelect;
